import { urlFor } from "lib/sanity";
import { useRef, useState, useEffect, useCallback } from "react";
import Carousel, { consts } from "react-elastic-carousel";
import CircleButton from "../components/CircleButton";
import ImageGallery from "react-image-gallery";
import { Modal } from "react-responsive-modal";
import "react-image-gallery/styles/css/image-gallery.css";
import CloseButton from "assets/svgs/close-button.svg";
import ReactYouTube from "react-youtube";
import getYouTubeId from "get-youtube-id";
import PlayIcon from "assets/svgs/play.svg";

export default function MediaGallery({ media }) {
  // Carousel states
  const [nextEnabled, setNextEnabled] = useState(true);
  const [prevEnabled, setPrevEnabled] = useState(true);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);
  const carouselRef = useRef();

  // Handles arrow enabling/disabling on resize and slide
  const setCarouselArrows = (future = false) => {
    const adjust = future ? 1 : 0;
    const activeIndex = carouselRef.current.state.activeIndex;
    const canSlidePrev =
      activeIndex !==
      carouselRef.current.getNextItemIndex(activeIndex - adjust, true);
    const canSlideNext =
      activeIndex !==
      carouselRef.current.getNextItemIndex(activeIndex + adjust, false);
    setNextEnabled(canSlideNext);
    setPrevEnabled(canSlidePrev);
  };

  // Sets the initial arrows
  useEffect(() => {
    const { children, itemsToShow } =
      carouselRef.current.getDerivedPropsFromBreakPoint();
    setNextEnabled(children.length > itemsToShow);
  }, []);

  const breakPoints = [
    { width: 1, itemsToShow: 2 },
    { width: 600, itemsToShow: 3 },
    { width: 768, itemsToShow: 4 },
    { width: 1024, itemsToShow: 5 },
  ];

  const handleResize = () => {
    setCarouselArrows();
  };

  const handleSlideNext = () => {
    carouselRef.current.slideNext();
    setCarouselArrows(true);
  };

  const handleSlidePrevious = () => {
    carouselRef.current.slidePrev();
    setCarouselArrows(true);
  };

  const handleOnChange = () => {
    setCarouselArrows();
  };

  const handleOpenGallery = useCallback(
    (index) => {
      setGalleryStartIndex(index);
      setIsGalleryOpen(true);
    },
    [setIsGalleryOpen, setGalleryStartIndex]
  );

  const handleCloseGallery = useCallback(() => {
    setIsGalleryOpen(false);
  }, [setIsGalleryOpen]);

  const carouselThumbnails = {
    image: ({ _key, image, alt, caption }, index) => {
      return (
        <figure
          key={_key}
          className="w-full relative"
          onClick={() => handleOpenGallery(index)}
        >
          <img
            src={urlFor(image).size(500, 500).auto("format").url()}
            alt={alt}
          />
          <div className="bg-opacity-0 hover:bg-opacity-50 duration-300 mb-1.5 p-1.5 absolute flex items-end inset-0 z-10 bg-black text-sm text-white font-semibold text-opacity-0 hover:text-opacity-75 cursor-pointer">
            <div className="line-clamp-3">{caption}</div>
          </div>
        </figure>
      );
    },
    youtube: ({ _key, url, alt, caption }, index) => {
      const videoId = getYouTubeId(url);
      const imageUrl = `https://img.youtube.com/vi/${videoId}/0.jpg`;
      return (
        <figure
          key={_key}
          className="w-full relative bg-black flex items-center justify-center"
          style={{ height: 0, paddingTop: "100%" }}
          onClick={() => handleOpenGallery(index)}
        >
          <div className="flex absolute top-0 min-h-full min-w-full max-h-full max-w-full">
            <img className="object-contain" src={imageUrl} alt={alt} />
          </div>
          <div className="bg-opacity-0 hover:bg-opacity-50 duration-300 mb-1.5 p-1.5 absolute flex items-end inset-0 z-20 bg-black text-sm text-white font-semibold text-opacity-0 hover:text-opacity-75 cursor-pointer">
            {caption}
          </div>
          <PlayIcon className="z-10 absolute" style={{ top: "40%" }} />
        </figure>
      );
    },
  };

  const galleryItems = {
    image: ({ image, caption }) => ({
      original: urlFor(image).auto("format").url(),
      thumbnail: urlFor(image).size(150, 150).auto("format").url(),
      description: caption,
      originalClass: "media-gallery-content",
    }),
    youtube: ({ url, caption, alt }) => {
      const videoId = getYouTubeId(url);
      const imageUrl = `https://img.youtube.com/vi/${videoId}/0.jpg`;
      return {
        thumbnail: imageUrl,
        description: caption,
        embedUrl: url,
        renderThumbInner: ({ thumbnail }) => {
          return (
            <div className="relative flex items-center justify-center">
              <div className="inline-block max-w-full overflow-hidden relative box-border m-0">
                <div className="box-border block max-w-full">
                  <img
                    className="max-h-full block m-0 border-none p-0"
                    alt
                    aria-hidden="true"
                    src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2ZXJzaW9uPSIxLjEiLz4="
                  />
                </div>
                <img
                  src={thumbnail}
                  alt={alt}
                  className="bg-black object-contain absolute m-auto block w-0 h-0 min-w-full max-w-full min-h-full max-h-full top-0"
                />
              </div>

              <PlayIcon className="absolute" />
            </div>
          );
        },
        renderItem: () => {
          return (
            <ReactYouTube
              videoId={videoId}
              containerClassName="video-wrapper"
            />
          );
        },
      };
    },
  };

  return (
    <div className="max-w-6xl	mx-auto p-5 xl:px-ll0 bg-white border rounded">
      <header className="relative flex">
        <h2 className="highlight">Gallery</h2>
        <div className="flex absolute right-0 top-0">
          <CircleButton
            disabled={!prevEnabled}
            text="slide left"
            onClick={handleSlidePrevious}
          />
          <CircleButton
            disabled={!nextEnabled}
            className="ml-2.5"
            direction="right"
            text="slide right"
            onClick={handleSlideNext}
          />
        </div>
      </header>
      <Carousel
        outerSpacing={-8}
        itemPadding={[0, 15]}
        itemPosition={consts.START}
        ref={carouselRef}
        breakPoints={breakPoints}
        showArrows={false}
        pagination={false}
        className="flex mt-4 -mx-1.5"
        onResize={handleResize}
        onChange={handleOnChange}
        showEmptySlots
        preventDefaultTouchmoveEvent
      >
        {media.map((m, index) => carouselThumbnails[m.type](m, index))}
      </Carousel>
      <Modal
        open={isGalleryOpen}
        onClose={handleCloseGallery}
        center
        closeIcon={<CloseButton className="h-4 w-4 text-white" />}
      >
        <ImageGallery
          startIndex={galleryStartIndex}
          items={media.map((m) => galleryItems[m.type](m))}
          showPlayButton={false}
        />
      </Modal>
    </div>
  );
}
